import clsx from 'clsx';
import type { StaticImageData } from 'next/image';
import type { ReactNode } from 'react';
import { BREAKPOINT } from '@publishing/helpers';
import Badge from '@publishing/components/ui/Badge';
import { Picture } from '@publishing/components/ui/Picture';
import type { ResponsivePair } from '@publishing/helpers/responsive';
import s from './CardSlideFlex.module.scss';

/**
 * CardSlideFlex — 슬라이드용 텍스트 + 이미지 카드
 *
 * href 없이 슬라이드 안 콘텐츠만 렌더합니다.
 * 좌측 텍스트, 우측 이미지 형태이며 badge는 프리셋 variant가 아닌 직접 입력 텍스트만 사용합니다.
 *
 * @example
 * const item: CardSlideFlexItem = {
 *   title: '업무에 집중할 수 있는 공간',
 *   text: '필요한 서비스와 공간을 한 번에 제공합니다.',
 *   images: [img, imgMo],
 *   badge: 'NEW',
 * };
 */
export interface CardSlideFlexItem {
  title: ReactNode;
  text?: ReactNode;
  images: ResponsivePair<string | StaticImageData>;
  alt?: string;
  badge?: string;
  note?: string;
}

export type CardSlideFlexTheme = 'primary' | 'secondary';

interface CardSlideFlexProps {
  item: CardSlideFlexItem;
  theme?: CardSlideFlexTheme;
  className?: string;
}

export default function CardSlideFlex({ item, theme = 'primary', className }: CardSlideFlexProps) {
  const [img, imgMo] = item.images;
  return (
    <div
      className={clsx(s.card_slide_flex, s[`card_slide_flex_${theme}`], className)}
      data-card-slide-flex=""
    >
      <div className={s.card_slide_flex_content}>
        <strong className={s.card_slide_flex_title}>{item.title}</strong>
        {item.text && (
          <p className={s.card_slide_flex_text}>{item.text}</p>
        )}
        {item.note && <span className={s.card_slide_flex_note}>{item.note}</span>}
      </div>

      <div className={s.card_slide_flex_image_area}>
        <Picture
          src={img}
          mobileSrc={imgMo}
          breakpoint={BREAKPOINT}
          alt={item.alt ?? ''}
          width={772}
          height={400}
        />
        {item.badge && (
          <Badge size={['xlarge', 'medium']} className={s.card_slide_flex_badge}>
            {item.badge}
          </Badge>
        )}
      </div>
    </div>
  );
}
